import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import HealthSummary from '../components/HealthSummary';
import CostByProviderChart from '../components/CostByProviderChart';
import { api } from '../utils/api';
import { 
  LuServer, 
  LuCloud, 
  LuArrowUpRight, 
  LuArrowDownRight,
  LuArrowRight,
  LuTrendingUp,
  LuTrendingDown,
  LuMinus,
  LuShield,
  LuCircleAlert,
  LuCircleCheck,
  LuCircle,
  LuDatabase,
  LuHardDrive,
  LuGlobe,
  LuActivity,
  LuZap,
  LuArchive,
  LuLink,
  LuEye
} from 'react-icons/lu';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { summarizeResourceCosts } from '../utils/costCoverage';

// Intelligence Statement Component
const IntelligenceStatement = ({ analytics, resources, providers }) => {
  const generateInsight = () => {
    const costSummary = summarizeResourceCosts(resources || []);
    const totalCost = costSummary.total;
    const completeCostCoverage = costSummary.pricedCount === (resources?.length || 0);
    const resourceCount = resources?.length || 0;
    const runningCount = analytics?.running_resources || 0;
    const stoppedCount = analytics?.stopped_resources || 0;
    const uncertainCount = (analytics?.unknown_resources || 0) + (analytics?.degraded_resources || 0);
    const transitioningCount = analytics?.transitioning_resources || 0;
    const staleCount = resources?.filter(resource => resource.stale).length || 0;
    const costByType = analytics?.cost_by_type || {};
    
    // No infrastructure
    if (resourceCount === 0) {
      return {
        type: 'neutral',
        primary: 'Connect your first cloud provider',
        secondary: 'Cloud Watcher will analyze your infrastructure automatically',
        icon: LuCloud
      };
    }
    
    if (uncertainCount > 0 || staleCount > 0) {
      return {
        type: 'alert',
        primary: `${Math.max(uncertainCount, staleCount)} resource state${Math.max(uncertainCount, staleCount) === 1 ? '' : 's'} need verification`,
        secondary: 'Cloud Watcher is preserving the last known state until providers respond',
        icon: LuCircleAlert
      };
    }

    if (transitioningCount > 0) {
      return {
        type: 'warning',
        primary: `${transitioningCount} provider operation${transitioningCount === 1 ? '' : 's'} in progress`,
        secondary: 'Observed state will update after provider reconciliation',
        icon: LuActivity
      };
    }

    // Stopped resources (high priority warning)
    if (stoppedCount > 0) {
      return {
        type: 'warning',
        primary: `${stoppedCount} stopped resource${stoppedCount > 1 ? 's' : ''} consuming no compute`,
        secondary: `Review documented purpose, applications, and ownership before retirement`,
        icon: LuCircleAlert
      };
    }
    
    // Single resource type dominance (cost pressure)
    const sortedTypes = Object.entries(costByType).sort((a, b) => b[1] - a[1]);
    if (completeCostCoverage && totalCost > 0 && sortedTypes[0] && sortedTypes[0][1] / totalCost > 0.70) {
      return {
        type: 'warning',
        primary: `${sortedTypes[0][0].replace('_', ' ')} dominates your spend`,
        secondary: `${Math.round((sortedTypes[0][1] / totalCost) * 100)}% of $${totalCost.toFixed(0)}/month · Review sizing and scaling`,
        icon: LuTrendingUp
      };
    }
    
    // All systems healthy
    return {
      type: 'positive',
      primary: `All observed resource states are operational`,
      secondary: `${runningCount} resource${runningCount !== 1 ? 's' : ''} observed running across ${providers?.length || 0} provider${providers?.length !== 1 ? 's' : ''}`,
      icon: LuCircleCheck
    };
  };

  const insight = useMemo(() => generateInsight(), [analytics, resources, providers]);
  
  const typeStyles = {
    positive: 'text-teal-400',
    warning: 'text-amber-400',
    alert: 'text-rose-400',
    neutral: 'text-zinc-400'
  };

  const Icon = insight.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
    >
      <div className="flex items-start gap-4">
        <div className="mt-1">
          <Icon className={`w-5 h-5 ${typeStyles[insight.type]}`} />
        </div>
        <div>
          <h1 className={`text-2xl md:text-3xl font-semibold tracking-tight ${typeStyles[insight.type]}`}>
            {insight.primary}
          </h1>
          <p className="text-base text-zinc-500 mt-1">
            {insight.secondary}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Living Infrastructure Map Component
const InfrastructureMap = ({ resources, providers }) => {
  const resourcesByType = useMemo(() => {
    const types = { vm: [], storage: [], database: [], network: [], load_balancer: [] };
    resources?.forEach(r => {
      if (types[r.type]) types[r.type].push(r);
    });
    return types;
  }, [resources]);

  const typeConfig = {
    vm: { label: 'Compute', icon: LuServer, color: '#3b82f6' },
    storage: { label: 'Storage', icon: LuHardDrive, color: '#14b8a6' },
    database: { label: 'Database', icon: LuDatabase, color: '#22c55e' },
    network: { label: 'Network', icon: LuGlobe, color: '#f59e0b' },
    load_balancer: { label: 'Load Balancer', icon: LuActivity, color: '#8b5cf6' }
  };

  const regions = useMemo(() => {
    const regionMap = {};
    resources?.forEach(r => {
      if (!regionMap[r.region]) {
        regionMap[r.region] = { count: 0, cost: 0, types: new Set() };
      }
      regionMap[r.region].count++;
      regionMap[r.region].cost += r.cost_per_month || 0;
      regionMap[r.region].types.add(r.type);
    });
    return Object.entries(regionMap).sort((a, b) => b[1].cost - a[1].cost);
  }, [resources]);

  return (
    <div className="glass-card rounded-xl p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-zinc-200">Infrastructure Topology</h2>
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Live</span>
      </div>

      {/* Resource Type Nodes */}
      <div className="flex flex-wrap gap-3 mb-8">
        {Object.entries(typeConfig).map(([type, config]) => {
          const count = resourcesByType[type]?.length || 0;
          const Icon = config.icon;
          if (count === 0) return null;
          
          return (
            <motion.div
              key={type}
              className="breathing"
              style={{ animationDelay: `${Math.random() * 2}s` }}
            >
              <div 
                className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                style={{ 
                  backgroundColor: `${config.color}15`,
                  borderColor: `${config.color}30`
                }}
              >
                <Icon className="w-4 h-4" style={{ color: config.color }} />
                <span className="text-sm font-medium" style={{ color: config.color }}>
                  {count}
                </span>
                <span className="text-xs text-zinc-500">{config.label}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Region Distribution */}
      <div className="space-y-3">
        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">By Region</p>
        {regions.slice(0, 4).map(([region, data], index) => (
          <motion.div
            key={region}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center gap-3"
          >
            <div 
              className="w-2 h-2 rounded-full pulse-slow"
              style={{ 
                backgroundColor: data.cost > 100 ? '#f59e0b' : '#14b8a6',
                animationDelay: `${index * 0.5}s`
              }}
            />
            <span className="text-sm text-zinc-300 flex-1 truncate">{region}</span>
            <span className="text-xs text-zinc-500">{data.count} resources</span>
            <span className="text-xs font-mono text-zinc-400">${data.cost.toFixed(0)}</span>
          </motion.div>
        ))}
        {regions.length === 0 && (
          <p className="text-sm text-zinc-600 text-center py-4">No resources discovered yet</p>
        )}
      </div>
    </div>
  );
};

// Cost Story Component
const CostStory = ({ analytics, resources, setProviderStatuses }) => {
  const costSummary = useMemo(() => summarizeResourceCosts(resources || []), [resources]);
  const { pricedCount, unknownCount: unknownCostCount, total: totalCost } = costSummary;
  const [sparklineData, setSparklineData] = useState([]);
  const trend = useMemo(() => {
    if (sparklineData.length < 2) return null;
    return sparklineData[sparklineData.length - 1].value >= sparklineData[0].value ? 'up' : 'down';
  }, [sparklineData]);

  useEffect(() => {
    let mounted = true;
    const fetchHistory = async () => {
      try {
        // Fetch last 14 days of cost history
        const end = new Date();
        const start = new Date(Date.now() - (13 * 24 * 60 * 60 * 1000)); // 14 points total
        const res = await api.getCostHistory(start.toISOString(), end.toISOString(), 'DAILY');
        // Backend returns { data: [ { date, cost } ], providers: [...] }
        const points = (res.data && res.data.data) || [];
        if (!mounted) return;

        if (points && points.length > 0) {
          // Map to sparkline format expected by recharts
          const mapped = points.slice(-14).map(p => ({ value: Number(p.cost || p.Cost || 0) }));
          setSparklineData(mapped);
        } else {
          setSparklineData([]);
        }
        // Set provider status info if caller provided setter
        try {
          const p = (res.data && res.data.providers) || (res.providers) || [];
          if (setProviderStatuses && p.length > 0) setProviderStatuses(p);
        } catch (err) {
          // ignore
        }
      } catch (e) {
        setSparklineData([]);
      }
    };

    fetchHistory();
    return () => { mounted = false; };
  }, [analytics]);
  
  // Calculate cost by type
  const costByType = useMemo(() => {
    const types = {};
    costSummary.priced.forEach(({ resource, amount }) => {
      types[resource.type] = (types[resource.type] || 0) + amount;
    });
    return Object.entries(types).sort((a, b) => b[1] - a[1]);
  }, [costSummary]);

  return (
    <div className="glass-card rounded-xl p-6 h-full">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">Monthly Cost</p>
        <div className={`flex items-center gap-1 text-xs ${
          trend == null ? 'text-zinc-500' : trend === 'up' ? 'text-amber-400' : 'text-teal-400'
        }`}>
          {trend == null ? 'No trend history' : trend === 'up' ? (
            <><LuTrendingUp className="w-3 h-3" /> trending up</>
          ) : (
            <><LuTrendingDown className="w-3 h-3" /> trending down</>
          )}
        </div>
      </div>
      
      {/* Main cost with sparkline */}
      <div className="relative mb-6">
        <div className="flex items-end gap-3">
          <span className="text-4xl font-semibold tracking-tight">
            {pricedCount ? `$${totalCost.toFixed(0)}` : '—'}
          </span>
          <span className="text-sm text-zinc-500 mb-1">{pricedCount ? '/month known' : 'monthly cost unknown'}</span>
        </div>
        
        {/* Sparkline */}
        {sparklineData.length > 1 && <div className="absolute right-0 top-0 w-24 h-12 opacity-60">
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={0}
            minHeight={48}
            initialDimension={{ width: 1, height: 48 }}
          >
            <AreaChart data={sparklineData}>
              <defs>
                <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={trend === 'up' ? '#f59e0b' : '#14b8a6'} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={trend === 'up' ? '#f59e0b' : '#14b8a6'} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={trend === 'up' ? '#f59e0b' : '#14b8a6'}
                strokeWidth={1.5}
                fill="url(#costGradient)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>}
      </div>

      {/* Cost breakdown */}
      <div className="space-y-2">
        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Breakdown</p>
        {costByType.slice(0, 3).map(([type, cost]) => (
          <div key={type} className="flex items-center justify-between">
            <span className="text-sm text-zinc-400 capitalize">{type.replace('_', ' ')}</span>
            <span className="text-sm font-mono text-zinc-300">${cost.toFixed(2)}</span>
          </div>
        ))}
      </div>

      {/* Cost insight */}
      {totalCost > 0 && (
        <div className="mt-6 pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500">
            {trend === 'up' 
              ? '↗ Known provider cost increased across the available history.'
              : '↘ Known provider cost decreased across the available history.'
            }
          </p>
          {unknownCostCount > 0 && <p className="mt-2 text-xs text-amber-300">Coverage: {pricedCount} of {resources.length} resources; {unknownCostCount} unknown.</p>}
        </div>
      )}
    </div>
  );
};

// Action Stream Component
const ActionStream = ({ resources, providers }) => {
  const generateRecommendations = () => {
    const recommendations = [];
    
    if (!providers || providers.length === 0) {
      recommendations.push({
        id: 'connect-provider',
        icon: LuLink,
        color: '#3b82f6',
        title: 'Connect your first cloud provider',
        description: 'Start with AWS, Azure, or a custom server',
        impact: 'Get started',
        confidence: 'high',
        action: '/providers'
      });
    }
    
    if (providers?.length === 1) {
      recommendations.push({
        id: 'add-provider',
        icon: LuCloud,
        color: '#8b5cf6',
        title: 'Unify cost visibility',
        description: 'Connect additional providers for complete overview',
        impact: 'Multi-cloud view',
        confidence: 'medium',
        action: '/providers'
      });
    }

    const storageResources = resources?.filter(r => r.type === 'storage') || [];
    if (storageResources.length > 2) {
      recommendations.push({
        id: 'archive-storage',
        icon: LuArchive,
        color: '#14b8a6',
        title: `Review ${storageResources.length} storage resources`,
        description: 'Review documented purpose, retention needs, and authoritative provider pricing.',
        impact: 'Manual review',
        confidence: 'low',
        action: '/resources?type=storage'
      });
    }

    const stoppedResources = resources?.filter(r => r.status === 'stopped') || [];
    if (stoppedResources.length > 0) {
      recommendations.push({
        id: 'cleanup-stopped',
        icon: LuServer,
        color: '#f59e0b',
        title: `${stoppedResources.length} stopped resource${stoppedResources.length > 1 ? 's' : ''} detected`,
        description: 'Consider terminating if no longer needed',
        impact: 'Reduce clutter',
        confidence: 'medium',
        action: '/resources?status=stopped'
      });
    }

    // Always show view resources if we have any
    if (resources?.length > 0) {
      recommendations.push({
        id: 'view-resources',
        icon: LuEye,
        color: '#71717a',
        title: 'Review all resources',
        description: `${resources.length} resources across your infrastructure`,
        impact: 'Full visibility',
        confidence: 'low',
        action: '/resources'
      });
    }

    return recommendations.slice(0, 4);
  };

  const recommendations = useMemo(() => generateRecommendations(), [resources, providers]);

  const confidenceStyles = {
    high: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
    medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    low: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
  };

  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuZap className="w-4 h-4 text-amber-400" />
        <h2 className="text-lg font-semibold text-zinc-200">Recommended Actions</h2>
      </div>

      <div className="space-y-3">
        {recommendations.map((rec, index) => {
          const Icon = rec.icon;
          return (
            <Link key={rec.id} to={rec.action}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ x: 4 }}
                className="flex items-start gap-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700 transition-all cursor-pointer group"
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${rec.color}15` }}
                >
                  <Icon className="w-5 h-5" style={{ color: rec.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-200 group-hover:text-white transition-colors">
                    {rec.title}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">{rec.description}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded border ${confidenceStyles[rec.confidence]}`}>
                    {rec.impact}
                  </span>
                  <LuArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </div>
              </motion.div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// Providers Capability View
const ProvidersCapability = ({ providers, providerStatuses = [] }) => {
  const getCapabilitySummary = (provider) => {
    // Use actual capabilities from provider data
    return provider.capabilities || {};
  };

  const renderStatusBadge = (prov) => {
    const status = providerStatuses.find(p => p.provider_id === prov.id);
    if (!status) return null;

    const s = status.status || 'unknown';
    const supports = status.supports_cost_history;

    const color = s === 'cached' || s === 'success' ? 'bg-teal-500/10 text-teal-400' : s === 'unsupported' ? 'bg-zinc-800 text-zinc-500' : 'bg-rose-500/10 text-rose-400';
    const label = supports ? (s === 'cached' ? 'CACHED' : s === 'success' ? 'HIST' : s.toUpperCase()) : 'NO-HIST';

    return (
      <span className={`text-xs px-2 py-0.5 rounded ${color} border ${s === 'error' ? 'border-rose-600/20' : 'border-teal-500/10'}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-zinc-200">Connected Infrastructure</h2>
        <Link to="/providers" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
          Manage →
        </Link>
      </div>

      {providers?.length === 0 ? (
        <div className="text-center py-6">
          <LuCloud className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 mb-4">No providers connected</p>
          <Link to="/providers">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm font-medium transition-colors"
            >
              Connect Provider
            </motion.button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {providers?.slice(0, 3).map((provider) => {
            const caps = getCapabilitySummary(provider);
            const activeCapabilities = Object.entries(caps).filter(([_, v]) => v);
            
            return (
              <div key={provider.id} className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-200 truncate">{provider.name}</span>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      provider.status === 'connected' ? 'bg-teal-400' : 'bg-zinc-600'
                    }`} />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {activeCapabilities.slice(0, 3).map(([cap]) => (
                      <span key={cap} className="text-xs text-zinc-500 capitalize">
                        {cap}
                      </span>
                    ))}
                    {activeCapabilities.length > 3 && (
                      <span className="text-xs text-zinc-600">+{activeCapabilities.length - 3}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {renderStatusBadge(provider)}
                  <Link to={`/providers`}>
                    <span className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Details</span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Activity & Health Component
const ActivityHealth = ({ resources }) => {
  const getHealthIndicator = (resource) => {
    // Determine health based on various factors
    const health = {
      status: resource.status === 'running' ? 'healthy' : 'warning',
      costRisk: resource.cost_per_month > 100 ? 'high' : resource.cost_per_month > 50 ? 'medium' : 'low',
      needed: resource.status === 'stopped' ? 'review' : 'active'
    };
    return health;
  };

  const typeIcons = {
    vm: LuServer,
    storage: LuHardDrive,
    database: LuDatabase,
    network: LuGlobe,
    load_balancer: LuActivity
  };

  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-zinc-200">Activity & Health</h2>
        <Link to="/resources" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
          View all →
        </Link>
      </div>

      {resources?.length === 0 ? (
        <div className="text-center py-6">
          <LuShield className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No resources to monitor</p>
        </div>
      ) : (
        <div className="space-y-2">
          {resources?.slice(0, 5).map((resource) => {
            const health = getHealthIndicator(resource);
            const Icon = typeIcons[resource.type] || LuServer;
            
            return (
              <Link key={resource.id} to={`/resources/${resource.id}`}>
                <motion.div
                  whileHover={{ x: 2 }}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-zinc-800/50 transition-all cursor-pointer group"
                >
                  {/* Health indicator */}
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    health.status === 'healthy' ? 'bg-teal-400' : 
                    health.status === 'warning' ? 'bg-amber-400' : 'bg-zinc-600'
                  } ${health.status === 'healthy' ? 'pulse-slow' : ''}`} />
                  
                  {/* Icon */}
                  <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  
                  {/* Name */}
                  <span className="text-sm text-zinc-300 truncate flex-1 group-hover:text-white transition-colors">
                    {resource.name}
                  </span>
                  
                  {/* Indicators */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {health.costRisk === 'high' && (
                      <span className="text-xs text-amber-400">$</span>
                    )}
                    {health.needed === 'review' && (
                      <span className="w-1 h-1 rounded-full bg-amber-400" />
                    )}
                    <span className="text-xs font-mono text-zinc-500">
                      ${resource.cost_per_month?.toFixed(0) || 0}
                    </span>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
const Dashboard = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [providers, setProviders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [providerStatuses, setProviderStatuses] = useState([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resourcesRes, providersRes, analyticsRes] = await Promise.all([
        api.getResources(),
        api.getProviders(),
        api.getAnalyticsSummary(),
      ]);

      setResources(resourcesRes.data || []);
      setProviders(providersRes.data || []);
      setAnalytics(analyticsRes.data);
      // reset provider statuses when analytics reloads
      setProviderStatuses([]);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Determine if system is in "quiet mode" (all healthy)
  const isQuietMode = useMemo(() => {
    const allRunning = resources.every(r => r.status === 'running' && !r.stale);
    return allRunning && resources.length > 0;
  }, [resources]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-zinc-700 border-t-amber-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500 text-sm">Loading infrastructure...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div role="alert" className="max-w-xl mx-auto px-4 py-20 text-center">
          <LuCircleAlert className="w-8 h-8 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-zinc-100">Infrastructure summary is unavailable</h1>
          <p className="text-sm text-zinc-500 mt-2 mb-6">A failed request has not been treated as empty infrastructure.</p>
          <button onClick={loadData} className="px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-zinc-950 ${isQuietMode ? 'quiet-mode' : 'alert-mode'}`}>
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Intelligence Statement - Full Width */}
        <IntelligenceStatement 
          analytics={analytics} 
          resources={resources} 
          providers={providers} 
        />

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Infrastructure Map */}
          <div className="col-span-1 lg:col-span-5">
            <InfrastructureMap resources={resources} providers={providers} />
          </div>

          {/* Right Column - Cost Story */}
          <div className="col-span-1 lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
              <CostStory analytics={analytics} resources={resources} setProviderStatuses={setProviderStatuses} />
              <ProvidersCapability providers={providers} providerStatuses={providerStatuses} />
            </div>
          </div>

          {/* Action Stream - Full Width */}
          <div className="col-span-1 lg:col-span-12">
            <ActionStream resources={resources} providers={providers} />
          </div>

          {/* Multi-Cloud Cost Comparison Chart - Full Width */}
          <div className="col-span-1 lg:col-span-12">
            <CostByProviderChart />
          </div>

          {/* Activity & Health + Resource Health Dashboard */}
          <div className="col-span-1 lg:col-span-7">
            <ActivityHealth resources={resources} />
          </div>

          {/* Resource Health Dashboard */}
          <div className="col-span-1 lg:col-span-5">
            <HealthSummary />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
